import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Space, Alert, Tag } from 'antd';
import AntTable from '../../components/CustomTable/AntTable';
import { getBlocklistedCounterparties, addAccountsToBlocklist, removeAccountFromBlocklist } from '../../services/fraudCheckService';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';

const { Search } = Input;
const { TextArea } = Input;

const BlocklistTab = () => {
  const [loading, setLoading] = useState(false);
  const [blocklist, setBlocklist] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // Modal form state
  const [modalVisible, setModalVisible] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();

  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchBlocklist = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBlocklistedCounterparties();
      const list = res?.data?.content || res?.data || res?.content || (Array.isArray(res) ? res : []);
      
      const formatted = list.map((item, index) => {
        if (typeof item === 'string') {
          return {
            account: item,
            id: item,
            reason: 'N/A',
            blockedBy: 'System / Admin',
            createdAt: null,
          };
        }
        return {
          ...item,
          id: item.id || item.account || `blocked_${index}`,
        };
      });

      setBlocklist(formatted);
      applyFilter(formatted, searchQuery);
    } catch (err) {
      setError(err.message || 'Failed to fetch blocklisted counterparties.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (list, query) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredList(list);
      setPagination(prev => ({ ...prev, total: list.length }));
      return;
    }
    const filtered = list.filter(item => 
      (item.account && item.account.toLowerCase().includes(q)) ||
      (item.reason && item.reason.toLowerCase().includes(q)) ||
      (item.blockedBy && item.blockedBy.toLowerCase().includes(q))
    );
    setFilteredList(filtered);
    setPagination(prev => ({ ...prev, total: filtered.length }));
  };

  useEffect(() => {
    fetchBlocklist();
  }, []);

  const handleSearch = (val) => {
    setSearchQuery(val);
    applyFilter(blocklist, val);
  };

  const handleTableChange = ({ pagination: newPagination }) => {
    setPagination(newPagination);
  };

  const handleOpenModal = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleAddSubmit = async (values) => {
    setFormLoading(true);
    try {
      // Parse accounts (allow comma separated values)
      const accountsStr = values.accounts || '';
      const accounts = accountsStr
        .split(',')
        .map(acc => acc.trim())
        .filter(acc => acc.length > 0);

      if (accounts.length === 0) {
        NotificationManager.error('Please enter at least one valid account number');
        setFormLoading(false);
        return;
      }

      const payload = {
        accounts,
        reason: values.reason,
      };

      const res = await addAccountsToBlocklist(payload);
      
      // Some APIs might return a success object or a message
      if (res && (res.success || res.status === true || !res.error)) {
        NotificationManager.success(res.message || 'Accounts successfully added to blocklist');
        handleCloseModal();
        fetchBlocklist();
      } else {
        NotificationManager.error(res?.message || 'Failed to add accounts to blocklist');
      }
    } catch (err) {
      NotificationManager.error(err.message || 'An error occurred while adding to blocklist');
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemove = (record) => {
    const accountNum = record.account;
    Swal.fire({
      title: 'Remove Account from Blocklist?',
      text: `Are you sure you want to remove account number ${accountNum} from the blocklist?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff4d4f',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Remove It',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await removeAccountFromBlocklist(accountNum);
          if (res && (res.success || res.status === true || !res.error)) {
            NotificationManager.success(res.message || 'Account successfully removed from blocklist');
            fetchBlocklist();
          } else {
            NotificationManager.error(res?.message || 'Failed to remove account from blocklist');
          }
        } catch (err) {
          NotificationManager.error(err.message || 'An error occurred while removing the account');
        }
      }
    });
  };

  const columns = [
    {
      title: 'Account Number',
      dataIndex: 'account',
      key: 'account',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Block Reason',
      dataIndex: 'reason',
      key: 'reason',
    },
    {
      title: 'Blocked By',
      dataIndex: 'blockedBy',
      key: 'blockedBy',
      render: (text) => text || 'System / Admin',
    },
    {
      title: 'Date Blocked',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A'),
    },
    {
      title: 'Status',
      key: 'status',
      render: () => <Tag color="red">BLOCKED</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="primary" danger size="small" onClick={() => handleRemove(record)}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <div className="fraud-toolbar">
        <Search
          placeholder="Search by account number, reason, blocked by..."
          allowClear
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 400 }}
        />
        <Space>
          <Button type="primary" onClick={handleOpenModal}>
            Add to Blocklist
          </Button>
          <Button type="default" onClick={fetchBlocklist} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      <AntTable
        loading={loading}
        users={filteredList}
        pagination={pagination}
        handleGetNext={handleTableChange}
        columns={columns}
      />

      <Modal
        title="Add Accounts to Blocklist"
        visible={modalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal} disabled={formLoading}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()} loading={formLoading}>
            Add Accounts
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddSubmit}
        >
          <Form.Item
            name="accounts"
            label="Account Number(s)"
            rules={[{ required: true, message: 'Please enter at least one account number' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter account number(s). For multiple accounts, separate with commas (e.g. 1020304050, 6070809000)"
            />
          </Form.Item>
          <div className="textarea-helper" style={{ marginTop: '-20px', marginBottom: '20px' }}>
            Tip: You can paste a comma-separated list of accounts.
          </div>

          <Form.Item
            name="reason"
            label="Reason for Blocklisting"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <Input placeholder="e.g. Suspicious transactions, fraudulent source" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BlocklistTab;
